import React, { useState, useCallback, useMemo } from 'react';
import { Button, PromptInput, Link } from '@cloudscape-design/components';
import { InputProps } from '@cloudscape-design/components/input';
import { useInputHistory } from 'src/hooks/useInputHistory';
import { useCopilot } from './CopilotContext';
import './Copilot.scss';

export function CopilotMessageInput() {
  const { state, isResponding, sendMessage, stopResponding } = useCopilot();
  const [inputValue, setInputValue] = useState('');

  // The user's own prior messages (oldest → newest) drive Up/Down history recall.
  const submittedMessages = useMemo(
    () => state.messages.filter((m) => m.role === 'user').map((m) => m.content),
    [state.messages],
  );
  const history = useInputHistory(submittedMessages);

  const handleSend = useCallback(() => {
    // Block while a turn is in progress so we never start a second concurrent stream —
    // this guards the Enter-key path (onAction) too, not just the (disabled) button.
    if (inputValue.trim() && !isResponding) {
      sendMessage(inputValue);
      setInputValue('');
      history.reset();
    }
  }, [inputValue, isResponding, sendMessage, history]);

  const handleDraftChange = useCallback(
    (value: string) => {
      // A user edit means we're back on a live draft, not browsing history.
      history.reset();
      setInputValue(value);
    },
    [history],
  );

  const handleKeyDown = useCallback(
    (event: CustomEvent<InputProps.KeyDetail>) => {
      // PromptInput natively submits on Enter, inserts a newline on Shift+Enter, and guards
      // IME composition — all routed through onAction. Up/Down step through the user's prior
      // messages (standard chat history): newest → oldest and back down to the empty draft.
      const recalled =
        event.detail.key === 'ArrowUp'
          ? history.recallOlder(inputValue)
          : event.detail.key === 'ArrowDown'
            ? history.recallNewer()
            : null;
      if (recalled !== null) {
        setInputValue(recalled);
        event.preventDefault();
      }
    },
    [history, inputValue],
  );

  return (
    <>
      <PromptInput
        value={inputValue}
        onChange={({ detail }) => handleDraftChange(detail.value)}
        onAction={handleSend}
        onKeyDown={handleKeyDown}
        placeholder="Ask about audits, tasks, questions..."
        disabled={isResponding}
        minRows={2}
        maxRows={6}
        autoFocus
        actionButtonIconName="send"
        disableActionButton={!inputValue.trim()}
        i18nStrings={{ actionButtonAriaLabel: 'Send message' }}
        customPrimaryAction={
          isResponding ? (
            // Replaces the send action while a response streams. A Cloudscape icon Button
            // so it inherits the design system's sizing, color, and focus/hover states and
            // sits naturally in the action slot. The stop glyph is a stroked rounded square
            // (Cloudscape has no stop icon) — Cloudscape applies stroke="currentColor", so
            // it matches the line-icon aesthetic of the send icon rather than a filled block.
            <Button
              variant="icon"
              ariaLabel="Stop response"
              onClick={stopResponding}
              iconSvg={
                <svg viewBox="0 0 16 16" focusable="false">
                  <rect x="4.5" y="4.5" width="7" height="7" rx="1.5" />
                </svg>
              }
            />
          ) : undefined
        }
      />
      <p className="copilot-input-disclaimer">
        Uses generative AI. Verify responses.{' '}
        <Link
          href="https://policy.a2z.com/docs/643450/publication"
          external
          fontSize="inherit"
          variant="primary"
        >
          Amazon AI Policy
        </Link>
      </p>
    </>
  );
}
